import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Button,
  Form,
  Input,
  Modal,
  Popconfirm,
  Select,
  Space,
  Switch,
  Table,
  Tag,
  Tooltip,
  Typography,
  message,
} from 'antd';
import { PlusOutlined } from '@ant-design/icons';
import { useLiveopsChange } from '@/hooks/useLiveopsChange';
import { adminApi } from '@/services/api';
import { apiErrorMessage } from '@/utils/apiError';
import { createLatestGuard } from '@/utils/latestRequest';
import {
  LANGUAGES,
  LANGUAGE_LABEL,
  PUSH_FONT,
  TEMPLATE_MOMENTS,
  TEMPLATE_MOMENT_LABEL,
  copyProblems,
} from '@/utils/matchPushCopy';
import type {
  MatchPushTemplate,
  MatchPushTemplateBody,
  PushLanguage,
  TemplateMoment,
} from '@/types/matchPushes';
import { CopyEditor } from './CopyEditor';
import type { CopyValue } from './CopyEditor';

const { Text } = Typography;

interface EditorState {
  /** null = a new template. */
  template: MatchPushTemplate | null;
}

interface FormValues {
  moment: TemplateMoment;
  language: PushLanguage;
  version: string;
  regions: string[];
  active: boolean;
}

const MOMENT_ORDER = new Map(TEMPLATE_MOMENTS.map((m, i) => [m, i]));
const LANGUAGE_ORDER = new Map(LANGUAGES.map((l, i) => [l, i]));

const TemplateModal: React.FC<{
  state: EditorState | null;
  onClose: () => void;
  onSaved: (template: MatchPushTemplate) => void;
}> = ({ state, onClose, onSaved }) => {
  const [form] = Form.useForm<FormValues>();
  const [copy, setCopy] = useState<CopyValue>({ title: '', body: '' });
  const [saving, setSaving] = useState(false);
  const template = state?.template ?? null;
  const language = Form.useWatch('language', form) ?? template?.language ?? 'en';

  useEffect(() => {
    if (!state) return;
    form.resetFields();
    form.setFieldsValue(
      template
        ? {
            moment: template.moment,
            language: template.language,
            version: template.version,
            regions: template.regions,
            active: template.active,
          }
        : { moment: 'toss', language: 'en', version: 'A', regions: [], active: true }
    );
    setCopy(template ? { title: template.title, body: template.body } : { title: '', body: '' });
  }, [state, template, form]);

  const problems = copyProblems(copy.title, copy.body, language);

  const save = async () => {
    let values: FormValues;
    try {
      values = await form.validateFields();
    } catch {
      return; // the form shows which fields to fix
    }
    setSaving(true);
    try {
      const regions = (values.regions ?? []).map((r) => r.trim()).filter(Boolean);
      if (template) {
        const body: MatchPushTemplateBody = {
          title: copy.title,
          body: copy.body,
          regions,
          active: values.active,
        };
        const response = await adminApi.matchPushes.updateTemplate(template.id, body);
        onSaved(response.data);
      } else {
        const response = await adminApi.matchPushes.createTemplate({
          moment: values.moment,
          language: values.language,
          version: values.version.trim(),
          title: copy.title,
          body: copy.body,
          regions,
          active: values.active,
        });
        onSaved(response.data);
      }
      message.success('Saved');
    } catch (error) {
      message.error(apiErrorMessage(error, 'Failed to save the template'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      open={state !== null}
      width={640}
      title={template ? 'Edit copy' : 'New copy version'}
      okText="Save"
      okButtonProps={{ disabled: problems.length > 0, loading: saving }}
      onOk={save}
      onCancel={onClose}
      destroyOnClose
    >
      <Form form={form} layout="vertical">
        <Space size={12} align="start" wrap>
          <Form.Item name="moment" label="Moment" rules={[{ required: true }]}>
            <Select
              disabled={Boolean(template)}
              style={{ width: 160 }}
              options={TEMPLATE_MOMENTS.map((m) => ({ value: m, label: TEMPLATE_MOMENT_LABEL[m] }))}
            />
          </Form.Item>
          <Form.Item name="language" label="Language" rules={[{ required: true }]}>
            <Select
              disabled={Boolean(template)}
              style={{ width: 130 }}
              options={LANGUAGES.map((l) => ({ value: l, label: LANGUAGE_LABEL[l] }))}
            />
          </Form.Item>
          <Form.Item
            name="version"
            label="Version"
            rules={[
              { required: true, message: 'Version' },
              { max: 4, message: 'At most 4 characters' },
            ]}
          >
            <Input
              disabled={Boolean(template)}
              maxLength={4}
              style={{ width: 80 }}
              placeholder="A"
            />
          </Form.Item>
          <Form.Item name="active" label="Active" valuePropName="checked">
            <Switch />
          </Form.Item>
        </Space>
        <Form.Item
          name="regions"
          label="Regions"
          extra="Leave empty for everyone. Players in a listed region get this text first."
        >
          <Select
            mode="tags"
            open={false}
            tokenSeparators={[',']}
            placeholder="e.g. Uttar Pradesh"
          />
        </Form.Item>
      </Form>
      <CopyEditor language={language} value={copy} onChange={setCopy} />
    </Modal>
  );
};

/** Editable table of the push copy: moment × language × version, with an active toggle. */
export const CopyTab: React.FC = () => {
  const [items, setItems] = useState<MatchPushTemplate[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [momentFilter, setMomentFilter] = useState<TemplateMoment | undefined>();
  const [languageFilter, setLanguageFilter] = useState<PushLanguage | undefined>();
  const [editor, setEditor] = useState<EditorState | null>(null);
  const [togglingId, setTogglingId] = useState<string | null>(null);

  const loadGuard = useRef(createLatestGuard());
  const { canChange, deniedTitle } = useLiveopsChange();

  const load = useCallback(async () => {
    const isLatest = loadGuard.current.begin();
    setLoading(true);
    setError(null);
    try {
      const response = await adminApi.matchPushes.listTemplates();
      if (isLatest()) setItems(response.data.items);
    } catch (e) {
      if (isLatest()) setError(apiErrorMessage(e, 'Failed to load the copy'));
    } finally {
      if (isLatest()) setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const rows = useMemo(
    () =>
      items
        .filter(
          (t) =>
            (!momentFilter || t.moment === momentFilter) &&
            (!languageFilter || t.language === languageFilter)
        )
        .sort(
          (a, b) =>
            (MOMENT_ORDER.get(a.moment) ?? 0) - (MOMENT_ORDER.get(b.moment) ?? 0) ||
            (LANGUAGE_ORDER.get(a.language) ?? 0) - (LANGUAGE_ORDER.get(b.language) ?? 0) ||
            a.version.localeCompare(b.version)
        ),
    [items, momentFilter, languageFilter]
  );

  const toggleActive = async (template: MatchPushTemplate, active: boolean) => {
    setTogglingId(template.id);
    try {
      const response = await adminApi.matchPushes.updateTemplate(template.id, { active });
      setItems((list) => list.map((t) => (t.id === template.id ? response.data : t)));
    } catch (e) {
      // e.g. 409 when this is the last active English template of a moment.
      message.error(apiErrorMessage(e, 'Failed to turn the copy off'));
    } finally {
      setTogglingId(null);
    }
  };

  const columns = [
    {
      title: 'Moment',
      dataIndex: 'moment',
      width: 120,
      render: (m: TemplateMoment) => TEMPLATE_MOMENT_LABEL[m],
    },
    {
      title: 'Language',
      dataIndex: 'language',
      width: 90,
      render: (l: PushLanguage) => LANGUAGE_LABEL[l],
    },
    { title: 'Ver.', dataIndex: 'version', width: 56 },
    {
      title: 'Copy',
      key: 'copy',
      render: (_: unknown, t: MatchPushTemplate) => (
        <div style={{ fontFamily: PUSH_FONT }}>
          <div style={{ fontWeight: 600 }}>{t.title}</div>
          <div>{t.body}</div>
        </div>
      ),
    },
    {
      title: 'Regions',
      dataIndex: 'regions',
      width: 150,
      render: (regions: string[]) =>
        regions.length ? (
          <Space size={2} wrap>
            {regions.map((r) => (
              <Tag key={r} style={{ marginInlineEnd: 0 }}>
                {r}
              </Tag>
            ))}
          </Space>
        ) : (
          <Text type="secondary">everyone</Text>
        ),
    },
    {
      title: 'Active',
      key: 'active',
      width: 80,
      render: (_: unknown, t: MatchPushTemplate) => (
        <Popconfirm
          disabled={!t.active || !canChange}
          title="Turn this copy off?"
          description="Players will no longer get this version."
          okText="Turn off"
          onConfirm={() => toggleActive(t, false)}
        >
          <Tooltip title={deniedTitle}>
            <Switch
              size="small"
              checked={t.active}
              loading={togglingId === t.id}
              disabled={!canChange}
              onChange={(on) => {
                if (on) toggleActive(t, true);
              }}
            />
          </Tooltip>
        </Popconfirm>
      ),
    },
    {
      title: '',
      key: 'edit',
      width: 70,
      render: (_: unknown, t: MatchPushTemplate) => (
        <Tooltip title={deniedTitle}>
          <Button
            type="link"
            size="small"
            disabled={!canChange}
            onClick={() => setEditor({ template: t })}
          >
            Edit
          </Button>
        </Tooltip>
      ),
    },
  ];

  return (
    <div>
      <Space wrap style={{ marginBottom: 12, width: '100%', justifyContent: 'space-between' }}>
        <Space wrap>
          <Select
            allowClear
            placeholder="All moments"
            style={{ width: 160 }}
            value={momentFilter}
            onChange={setMomentFilter}
            options={TEMPLATE_MOMENTS.map((m) => ({ value: m, label: TEMPLATE_MOMENT_LABEL[m] }))}
          />
          <Select
            allowClear
            placeholder="All languages"
            style={{ width: 150 }}
            value={languageFilter}
            onChange={setLanguageFilter}
            options={LANGUAGES.map((l) => ({ value: l, label: LANGUAGE_LABEL[l] }))}
          />
          <Text type="secondary">
            Players get one active version per language, picked at random; versions are measured
            against each other on the Results tab.
          </Text>
        </Space>
        <Tooltip title={deniedTitle}>
          <Button
            type="primary"
            icon={<PlusOutlined />}
            disabled={!canChange}
            onClick={() => setEditor({ template: null })}
          >
            New version
          </Button>
        </Tooltip>
      </Space>
      {error && <Alert type="error" showIcon message={error} style={{ marginBottom: 12 }} />}
      <Table
        size="small"
        rowKey="id"
        loading={loading}
        columns={columns}
        dataSource={rows}
        pagination={false}
        scroll={{ x: 'max-content' }}
      />
      <TemplateModal
        state={editor}
        onClose={() => setEditor(null)}
        onSaved={(saved) => {
          setItems((list) =>
            list.some((t) => t.id === saved.id)
              ? list.map((t) => (t.id === saved.id ? saved : t))
              : [...list, saved]
          );
          setEditor(null);
        }}
      />
    </div>
  );
};
